import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiPost } from '@/lib/http/client';

export type OutlookResource = 'mail' | 'calendar';

interface StartArgs {
  socialAccountId: number;
  resource: OutlookResource;
}

interface StartResult {
  result: unknown;
  capturedOrgId: string;
}

export function useOutlookStartImport(orgId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<StartResult, Error, StartArgs>({
    // Capture the org used at mutation kick-off so a mid-flight org switch
    // can't invalidate the wrong org on completion.
    mutationFn: async ({ socialAccountId, resource }: StartArgs) => {
      if (!orgId) {
        throw new Error('No org selected');
      }
      const capturedOrgId = orgId;
      const result = await apiPost(
        `/api/orgs/${capturedOrgId}/msgraph/import-jobs/start/`,
        {
          social_account_id: socialAccountId,
          resource,
        }
      );
      return { result, capturedOrgId };
    },
    onSuccess: (data: StartResult) => {
      const { capturedOrgId } = data;
      queryClient.invalidateQueries({
        queryKey: ['microsoft', 'outlook', 'status', capturedOrgId],
      });
      // `useImportStatus` keys on ['import-status', orgId]; that's the query
      // backing the import-status UI, so it must be invalidated here for the
      // newly-started job to appear without a manual refresh.
      queryClient.invalidateQueries({
        queryKey: ['import-status', capturedOrgId],
      });
    },
  });
}
